import { type ChangeEvent, type FocusEvent, useEffect, useState } from 'react'
import { KibField, type KibFieldProps } from '@chewy/kib-fields-new-react'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import styles from './PaymentMethodSection.module.scss'

type NumberFieldProps = Omit<KibFieldProps, 'onChange' | 'min' | 'step'> & {
  isMoney?: boolean
  maxLength?: number
  min?: number
  onChange?: (value: string) => void
  step?: string
  validationMessage?: string | null
}

const NumberField = ({
  onChange,
  isMoney = false,
  maxLength,
  validationMessage,
  min,
  step,
  value,
  readOnly = false,
  disabled,
  ...kibFieldProps
}: NumberFieldProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [localValue, setLocalValue] = useState<string>(value?.toString() || '')
  const [formatKey, setFormatKey] = useState(0)
  const [hasInteracted, setHasInteracted] = useState(false)

  // Sync localValue with prop value when not focused
  useEffect(() => {
    if (!isFocused) {
      const currentValue = value?.toString() || ''
      setLocalValue(currentValue)
      setFormatKey(0)
    }
  }, [value, isFocused])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value
    if (maxLength !== undefined && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength)
    }

    setLocalValue(newValue)
    onChange?.(newValue)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    setHasInteracted(true)
    if (isMoney && e.target.value) {
      const numValue = parseFloat(e.target.value)
      if (!Number.isNaN(numValue)) {
        // Format to 2 decimal places on blur to preserve trailing zeros
        const formatted = numValue.toFixed(2)

        // Increment key when formatting to force Chirp re-validation
        // This clears Chirp's internal validation state (like 3 decimals error)
        setFormatKey((prev) => prev + 1)
        setLocalValue(formatted)
        onChange?.(formatted)
      }
    }
  }

  const fieldStep = step || (isMoney ? '0.01' : '1')
  const fieldMin = min !== undefined ? min : isMoney ? 0.01 : 0

  const displayValue =
    (readOnly || (isMoney && !isFocused)) && value && value !== ''
      ? (() => {
          const parsed = parseFloat(value.toString())
          return Number.isNaN(parsed) ? value : parsed.toFixed(2)
        })()
      : isFocused
        ? localValue
        : value

  const shouldShowValidation =
    !disabled && hasInteracted && Boolean(validationMessage) && !readOnly
  return (
    <div className={styles.fieldContainer}>
      <KibField
        {...kibFieldProps}
        disabled={disabled}
        invalid={shouldShowValidation}
        key={formatKey > 0 ? `format-${formatKey}` : undefined}
        messagesContent={
          shouldShowValidation ? (
            <p>{validationMessage}</p>
          ) : (
            kibFieldProps.messagesContent
          )
        }
        min={fieldMin}
        readOnly={readOnly}
        step={fieldStep}
        type="number"
        value={displayValue}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />
    </div>
  )
}

export default NumberField
